import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { v4 as uuidv4 } from 'uuid'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { OutputType } from '@/app/constants/outputType'
import { SERVICE_TIME_FORMAT } from '@/app/constants/date'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { usePrintJobStore } from '../stores/printJobStore'
import { getReportTypeByNumber } from '@/app/services/printout/reportTypeResolver'
import { withPrintoutData } from '../utils/reportDefaultParams'
import { takeRangeOverride } from '../utils/printoutRangeOverride'

const GROUP_ACCESS_CODES = ['REPORT_NOT_AVAILABLE_FOR_GROUP_ACCESS', 'NO_DRIVERS_IN_GROUPS', 'NO_VEHICLES_IN_GROUPS']

export interface PrintPdfReportJob {
  reportType: number
  data?: Record<string, unknown>
}

export interface PrintPdfParams {
  companyId: string
  reportType: number
  drivers: Array<string> | null
  vehicles: Array<string> | null
  startTime: Moment
  endTime: Moment
  outputType: OutputType
  data?: any
  sendByEmail?: boolean
  reports?: PrintPdfReportJob[]
}

export interface UsePrintPdfResult {
  printPdf: (params: PrintPdfParams) => Promise<any>
  loading: boolean
  error: string | null
}

export function usePrintPdf(): UsePrintPdfResult {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const { activeLanguage, t } = useLocalize()

  const printPdf = React.useCallback(async (params: PrintPdfParams) => {
    const {
      companyId,
      reportType,
      drivers,
      vehicles,
      startTime,
      endTime,
      outputType,
      data = {},
      sendByEmail = false,
      reports,
    } = params

    setLoading(true)
    setError(null)

    const finishJob = usePrintJobStore.getState().beginJob()

    try {
      const id = uuidv4()


      const override = takeRangeOverride(data)
      const reportData = override.data
      const effectiveStartTime =
        !reports && override.startTime ? override.startTime : startTime
      const effectiveEndTime =
        !reports && override.endTime ? override.endTime : endTime

      const payload = {
        id,
        drivers: drivers ? drivers.map((d) => +d) : null,
        vehicles: vehicles ? vehicles.map((d) => +d) : null,
        reportType,
        startTime: moment(effectiveStartTime).format(SERVICE_TIME_FORMAT),
        endTime: moment(effectiveEndTime).format(SERVICE_TIME_FORMAT),
        outputType,
        data: withPrintoutData(getReportTypeByNumber(reportType), reportData),
        language: activeLanguage?.code,
        sendByEmail,
        reports: reports?.map((report) => ({
          reportType: report.reportType,
          data: withPrintoutData(
            getReportTypeByNumber(report.reportType),
            takeRangeOverride(report.data ?? {}).data,
          ),
        })),
      }

      const response = await axiosInstance.post(
        `print-api/${companyId}/printout/print`,
        payload,
      )

      usePrintJobStore.getState().trackTask(id)

      return { data: response.data, taskId: id }
    } catch (err: any) {
      // Kody z dostępu do wybranych grup mają tłumaczenie; reszta jak dotąd.
      const code: string | undefined = err?.response?.data?.code
      const message =
        err?.response?.data?.message ||
        (code && GROUP_ACCESS_CODES.includes(code) ? t(`errorCode.${code}`) : code) ||
        err?.message ||
        'Unknown error'
      setError(message)
      throw err
    } finally {
      setLoading(false)
      finishJob()
    }
  }, [activeLanguage, t])

  return { printPdf, loading, error }
}
